import React from 'react'
import { useNavigate } from 'react-router-dom'
import { urls } from '../../../urls'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'
import {
  Contacts,
  ExpandLess,
  ExpandMore,
  ContactMail,
  QueueRounded,
  RecentActors,
  Description,
  List as ListIcon,
  Schedule
} from '@material-ui/icons'
import { Collapse, Drawer, Tooltip, Typography } from '@material-ui/core'
import permissions from '../../../permissions.json'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import EntityContext from '../../../contexts/Entity'

// -------------- Styles --------------
const DRAWER_WIDTH_LG = 240
const DRAWER_WIDTH_MD = 220
const DRAWER_WIDTH_MOBILE = 280
const DRAWER_WIDTH_RAIL = 64

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Reserva el espacio del menú permanente dentro del layout flex
    drawer: {
      flexShrink: 0,
      width: DRAWER_WIDTH_LG,
      [theme.breakpoints.down('md')]: {
        width: DRAWER_WIDTH_MD,
      },
    },
    drawerRail: {
      width: DRAWER_WIDTH_RAIL,
      [theme.breakpoints.down('md')]: {
        width: DRAWER_WIDTH_RAIL,
      },
    },
    drawerMobile: {
      width: DRAWER_WIDTH_MOBILE,
    },
    paper: {
      backgroundColor: '#263238',
      color: '#eceff1',
      borderRight: 'none',
      overflowX: 'hidden',
    },
    paperMobile: {
      width: DRAWER_WIDTH_MOBILE,
      maxWidth: '85vw',
    },
    hidden: {
      display: 'none'
    },
    textPrimary: {
      color: '#ffff',
      fontSize: '1rem'
    },
    icon: {
      color: '#ffff',
      minWidth: 40,
    },
    iconRail: {
      color: '#ffff',
      minWidth: 0,
      justifyContent: 'center',
    },
    item: {
      minHeight: 48,
    },
    itemRail: {
      minHeight: 48,
      justifyContent: 'center',
      paddingLeft: 0,
      paddingRight: 0,
    },
    chevron: {
      color: '#b0bec5',
    },
    menuHeader: {
      textAlign: 'left',
      width: '100%',
    },
    entityName: {
      display: 'block',
      color: '#ffff',
      fontSize: '1.1rem',
      wordBreak: 'break-word',
      lineHeight: 1.3,
    },
    entityDocument: {
      display: 'block',
      color: '#29b6f6',
      fontSize: '0.9rem',
      marginTop: theme.spacing(0.5),
    },
    nestedList: {
      background: '#455a64'
    },
    nested: {
      paddingLeft: theme.spacing(4),
    },
    nestedRail: {
      paddingLeft: 0,
    },
  }),
)

interface MainMenuProps {
  // permanent: columna fija (>= sm) · temporary: drawer deslizable (xs)
  variant: 'permanent' | 'temporary'
  // true => rail de iconos (tablet vertical)
  collapsed: boolean
  // solo aplica a variant="temporary"
  open: boolean
  onClose: () => void
}

// - in this part all the component menu are housed
const initState = {
  selected: '',
  menu: {
    items: [
      {
        id: '003',
        title: 'Administración de terceros',
        icon: <Contacts />,
        permission: permissions.third.view,
        indexedMenu: {
          open: false,
          items: [
            {
              id: '003-01',
              title: 'Creación de terceros',
              icon: <ContactMail />,
              permission: permissions.third.create,
              url: urls.app.main.third.form
            },
            {
              id: '003-02',
              title: 'Listar terceros',
              icon: <RecentActors/>,
              permission: permissions.third.view,
              url: urls.app.main.third.list
            },
            {
              id: '003-03',
              title: 'Migración de terceros',
              icon: <ContactMail />,
              permission: permissions.third.migrate,
              url: urls.app.main.third.thirdMigrationForm
            }
          ]
        }
      },
      {
        id: '007',
        title: 'Productos',
        icon: <QueueRounded/>,
        permission: permissions.item.view,
        indexedMenu: {
          open: false,
          items: [
            {
              id: '007-01',
              title: 'Creación de productos',
              icon: <QueueRounded />,
              permission: permissions.item.create,
              url: urls.app.main.item.form
            },
            {
              id: '007-02',
              title: 'Listar productos',
              icon: <ListIcon/>,
              permission: permissions.item.view,
              url: urls.app.main.item.list
            },
          ]
        }
      },
      {
        id: '008',
        title: 'Factura electrónica',
        icon: <Description/>,
        permission: permissions.electronic_bill.view,
        indexedMenu: {
          open: false,
          items: [
            {
              id: '008-01',
              title: 'Crear factura electrónica',
              icon: <Description/>,
              permission: permissions.electronic_bill.view,
              url: urls.app.main.electronicBill.form
            },
            {
              id: '008-02',
              title: 'Listar factura electrónica',
              icon: <Description/>,
              permission: permissions.electronic_bill.view,
              url: urls.app.main.electronicBill.list
            },
            {
              id: '008-03',
              title: 'Listar programaciones',
              icon: <Schedule/>,
              permission: permissions.electronic_bill.schedule_view,
              url: urls.app.main.electronicBill.schedule_list
            },
            {
              id: '008-04',
              title: 'Listar notas crédito',
              icon: <Description/>,
              permission: permissions.electronic_bill.view,
              url: urls.app.main.electronicBill.credit_note_list
            }
          ]
        }
      },
      {
        id: '009',
        title: 'Documento soporte',
        icon: <Description/>,
        permission: permissions.electronic_bill.view,
        indexedMenu: {
          open: false,
          items: [
            {
              id: '009-01',
              title: 'Crear documento soporte',
              icon: <Description/>,
              permission: permissions.support_document.view,
              url: urls.app.main.supportDocument.form
            },
            {
              id: '009-02',
              title: 'Listar documento soporte',
              icon: <Description/>,
              permission: permissions.support_document.view,
              url: urls.app.main.supportDocument.list
            },
            {
              id: '009-03',
              title: 'Listar notas crédito de documento soporte',
              icon: <ListIcon/>,
              permission: permissions.support_document.list_credit_note,
              url: urls.app.main.supportDocument.credit_note_list
            },
          ]
        }
      }
    ]
  }
}

export default function MainMenu({ variant, collapsed, open, onClose }: MainMenuProps) {

  const classes = useStyles()
  const navigate = useNavigate()
  const [state, setState] = React.useState(initState)
  const { userContext } = React.useContext(
    UserContext
  ) as UserContextType
  const { entityContext } = React.useContext(EntityContext) as { entityContext: { name: string; document: string } }

  // matches: se muestran textos y chevrons (todo salvo el rail de iconos)
  const matches = !collapsed

  const handleListMenu = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: any,
  ) => {
    if (item.indexedMenu) {
      setState({
        ...state,
        selected: item.id,
        menu: {
          ...state.menu,
          items: state.menu.items.map(savedItem => {
            if (savedItem.id === item.id) {
              if (savedItem.indexedMenu) {
                savedItem.indexedMenu.open = !savedItem.indexedMenu.open
              }
            }
            return savedItem
          })
        }
      })
    } else {
      setState({
        ...state,
        selected: item.id
      })
      navigate(item.url)
      // en mobile el drawer se cierra al navegar
      if (variant === 'temporary') onClose()
    }
  }

  const menuContent = (
        <List
          component="nav"
          aria-label="Menú principal"
        >
          {matches && (
            <ListItem>
              <Typography component="div" className={classes.menuHeader}>
                <Typography component="div" className={classes.entityName}>{entityContext.name}</Typography>
                <Typography component="div" className={classes.entityDocument}>{entityContext.document}</Typography>
              </Typography>
            </ListItem>
          )}

          <Divider />
          {
            state.menu.items.map(item => (
              userContext.permissions.some(permission => permission === item.permission) ?
              (
                <React.Fragment key={item.id}>
                  <Tooltip title={item.title} placement="right" disableHoverListener={matches} disableFocusListener={matches} disableTouchListener={matches}>
                    <ListItem
                      button
                      className={matches ? classes.item : classes.itemRail}
                      selected={state.selected === item.id}
                      onClick={(event) => handleListMenu(event, item)}
                    >
                      <ListItemIcon
                        className={matches ? classes.icon : classes.iconRail}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        className={matches ? classes.textPrimary : classes.hidden}
                      />
                      {
                        item.indexedMenu && matches ?
                          item.indexedMenu.open ? <ExpandLess className={classes.chevron} /> : <ExpandMore className={classes.chevron} />
                          :
                          ''
                      }
                    </ListItem>
                  </Tooltip>
                  {
                    item.indexedMenu ?
                      (
                        <Collapse
                          in={item.indexedMenu.open}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List
                            component="div"
                            disablePadding
                            className={classes.nestedList}
                          >
                            {
                              item.indexedMenu.items.map(indexedItem => (
                                userContext.permissions.some(permission => permission === indexedItem.permission) ?
                                (
                                  <Tooltip
                                    key={indexedItem.id}
                                    title={indexedItem.title}
                                    placement="right"
                                    disableHoverListener={matches}
                                    disableFocusListener={matches}
                                    disableTouchListener={matches}
                                  >
                                    <ListItem
                                      button
                                      className={`${matches ? classes.item : classes.itemRail} ${matches ? classes.nested : classes.nestedRail}`}
                                      selected={state.selected === indexedItem.id}
                                      onClick={(event) => handleListMenu(event, indexedItem)}
                                    >
                                      <ListItemIcon
                                        className={matches ? classes.icon : classes.iconRail}
                                      >
                                        {indexedItem.icon}
                                      </ListItemIcon>
                                      <ListItemText
                                        className={matches ? classes.textPrimary : classes.hidden}
                                        primary={indexedItem.title}
                                      />
                                    </ListItem>
                                  </Tooltip>
                                ) : ''
                              ))
                            }
                          </List>
                        </Collapse>
                      ) : ''
                  }
                </React.Fragment>
              ) : ''
            ))
          }
        </List>
  )

  if (variant === 'temporary') {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        className={classes.drawerMobile}
        classes={{ paper: `${classes.paper} ${classes.paperMobile}` }}
      >
        {menuContent}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="permanent"
      className={collapsed ? classes.drawerRail : classes.drawer}
      // el panel es position:fixed, por eso necesita el mismo ancho que el contenedor
      classes={{ paper: `${classes.paper} ${collapsed ? classes.drawerRail : classes.drawer}` }}
    >
      {menuContent}
    </Drawer>
  )
}
